import type { Ledge } from './surfaces';

/**
 * Routing toward the favoured perch.
 *
 * The companion's standing objective is to end up on the assistant bar. That
 * is a routing question — "what is the next step toward it from where I am?" —
 * and it cannot be answered by choosing among locally available moves, because
 * the bar frequently shares no edge with whatever the companion is standing
 * on. Getting there means descending first, which no purely local choice will
 * ever pick.
 *
 * This module answers only that question, in one place, with no DOM and no
 * randomness. Whether the companion *wants* to go home on a given beat stays
 * with the caller.
 */

export type HomeStep =
  | { kind: 'arrived' }
  | { kind: 'walk'; x: number }
  | { kind: 'hop'; ledge: Ledge }
  | { kind: 'drop'; x: number };

export interface RouteInput {
  ledges: readonly Ledge[];
  standing: Ledge;
  x: number;
  /** Horizontal span of the favoured perch, or null when it is off screen. */
  favoured: { left: number; right: number } | null;
  petWidth: number;
  /** How far above the standing ledge a hop may reach. */
  maxRise: number;
  restY: (ledge: Ledge) => number;
}

const spans = (l: Ledge, fav: { left: number; right: number }) =>
  l.left <= fav.right && l.right >= fav.left;

const overlaps = (a: Ledge, b: Ledge) =>
  Math.min(a.right, b.right) > Math.max(a.left, b.left);

/**
 * The next move toward home.
 *
 * - `arrived` — already standing on the perch that holds it.
 * - `walk` — home is on this ledge, or on one this ledge can reach; close the
 *   horizontal gap first so the hop has something to aim at.
 * - `hop` — the perch is adjacent and within rising reach.
 * - `drop` — nothing here leads anywhere useful, so walk off the nearest edge
 *   and let gravity carry it down to a surface that does. Descending is always
 *   allowed; that is what stops the companion being marooned on a card.
 */
export function routeHome({
  ledges, standing, x, favoured, petWidth, maxRise, restY,
}: RouteInput): HomeStep {
  if (!favoured) return { kind: 'arrived' };

  // Home is the ledge the perch itself created, so it is the one whose span
  // most closely matches it. Merely overlapping is not enough: the floor and
  // a full-width tab bar both contain the perch horizontally without being it.
  // Each edge is measured on its own. Summing the two signed differences lets
  // them cancel, so a ledge missing the perch by the same margin on both sides
  // scores a perfect zero and beats the ledge that actually is the perch.
  const slack = (l: Ledge) =>
    Math.abs(favoured.left - l.left) + Math.abs(l.right - favoured.right);

  const home = ledges.reduce<Ledge | null>((best, l) => {
    if (!spans(l, favoured)) return best;
    if (best === null) return l;
    return slack(l) < slack(best) ? l : best;
  }, null);

  if (home === standing) {
    const lo = Math.max(standing.left, favoured.left);
    const hi = Math.min(standing.right, favoured.right);
    if (x >= lo && x <= hi) return { kind: 'arrived' };
    return { kind: 'walk', x: x < lo ? lo : hi };
  }

  if (home !== null) {
    const rise = restY(standing) - restY(home);
    if (rise <= maxRise) {
      const lo = Math.max(standing.left, home.left);
      const hi = Math.min(standing.right, home.right);
      // Within reach vertically. Hop if already underneath it, otherwise walk
      // into position first so the hop has something to aim at.
      if (hi >= lo) {
        if (overlaps(standing, home) && x >= lo && x <= hi) {
          return { kind: 'hop', ledge: home };
        }
        return { kind: 'walk', x: (lo + hi) / 2 };
      }
    }
  }

  // Home is below, or unreachable from this height. Step off the edge nearest
  // to it — one ledge at a time, this climbs down whatever it was dropped on.
  const homeCentre = (favoured.left + favoured.right) / 2;
  const offLeft = homeCentre < (standing.left + standing.right) / 2;
  return { kind: 'drop', x: offLeft ? standing.left - petWidth : standing.right + petWidth };
}
